import React, { useState, useEffect, useCallback } from 'react';
import { Plus, RefreshCw, X, CheckSquare, Square, Save, Edit2, ArrowRight, Users, Phone } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { SortHeader } from '../../components/SortHeader';
import { useSortableData } from '../../hooks/useSortableData';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_TASKS = {
  receptionist: [
    { category: 'Admin', description: 'Reception table neatness' },
    { category: 'Clinical/Admin', description: 'Appointment check and follow up' },
    { category: 'Admin', description: 'Admin checking' },
    { category: 'Closing', description: 'Today bill against wanted list check, Urology follow up' },
  ],
  nurse: [
    { category: 'Maintenance', description: 'Doctor Room table and observation room neatness' },
    { category: 'Clinical', description: 'Injection / Dressing / IV first' },
    { category: 'Inventory', description: 'Bill Stock checking every 3 days ernst and jemed check' },
    { category: 'Closing', description: 'Update in Aaric' },
  ],
  labtech: [
    { category: 'Maintenance', description: 'Lab room bench and scan neatness' },
    { category: 'Clinical', description: 'Lab data maintain / Scans data maintain' },
    { category: 'Inventory', description: 'Lab Wanted checking' },
    { category: 'Closing', description: 'Outsources sent & AMC Update' },
  ],
  pharmacist: [
    { category: 'Maintenance', description: 'Pharmacy table and shelf organization' },
    { category: 'Inventory', description: 'Pharmacy bill against wanted' },
    { category: 'Inventory', description: 'Wanted checking' },
    { category: 'Closing', description: 'Stock Entry / PO (should be done for all days)' },
  ],
  admin: [
    { category: 'Clinical/Admin', description: 'Review daily reports' },
    { category: 'Clinical/Admin', description: 'Approve wanted list items' },
    { category: 'Maintenance', description: 'Check facility status' },
    { category: 'Closing', description: 'End of day review' },
  ],
};

const fetchRoleTasks = async (role) => {
  try {
    const { data } = await axios.get(`${API}/staff/default-tasks/${role}`, { withCredentials: true });
    if (data.tasks && data.tasks.length > 0) return data.tasks;
  } catch { /* fallback */ }
  return FALLBACK_TASKS[role] || FALLBACK_TASKS.admin;
};

const ROLES = ['receptionist', 'pharmacist', 'nurse', 'labtech', 'admin'];
const DISPLAY_ROLES = [
  { key: 'nurse', label: 'Nurse' },
  { key: 'receptionist', label: 'Reception' },
  { key: 'labtech', label: 'Lab Tech' },
  { key: 'pharmacist', label: 'Pharmacist' },
];

const ROLE_COLORS = {
  receptionist: 'bg-blue-100 text-blue-700',
  pharmacist: 'bg-green-100 text-green-700',
  nurse: 'bg-pink-100 text-pink-700',
  labtech: 'bg-purple-100 text-purple-700',
  admin: 'bg-amber-100 text-amber-700',
};

const DailyTaskModule = ({ user }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedRole, setSelectedRole] = useState(user.role);
  const [tasks, setTasks] = useState([]);
  const [extraTasks, setExtraTasks] = useState([
    { category: 'Other', description: '', completed: false },
    { category: 'Other', description: '', completed: false },
    { category: 'Other', description: '', completed: false },
  ]);
  const [editId, setEditId] = useState(null);
  const [filterRole, setFilterRole] = useState('All');
  const [detailDate, setDetailDate] = useState(new Date().toISOString().split('T')[0]);
  const [dateDetail, setDateDetail] = useState([]);
  const [pickedTaskIds, setPickedTaskIds] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/staff/daily-tasks`, { withCredentials: true });
      setEntries(data);
    } catch { toast.error('Failed to load tasks'); }
    setLoading(false);
  }, []);

  const fetchDateDetail = useCallback(async (d) => {
    try {
      const { data } = await axios.get(`${API}/staff/daily-tasks/date-detail?date=${d}`, { withCredentials: true });
      setDateDetail(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchDateDetail(detailDate); }, [detailDate, fetchDateDetail]);

  const initNewForm = async () => {
    const role = user.role;
    setSelectedRole(role);
    const roleTasksData = await fetchRoleTasks(role);
    const roleTasks = roleTasksData.map(t => ({ ...t, completed: false }));
    setDate(new Date().toISOString().split('T')[0]);
    setEditId(null);
    try {
      const { data } = await axios.get(`${API}/staff/daily-tasks/pending/${role}`, { withCredentials: true });
      const pending = data.pending_tasks || [];
      const carryForward = pending.map(t => ({ category: 'Carried Forward', description: t.description, completed: false }));
      setTasks([...roleTasks, ...carryForward]);
      if (carryForward.length > 0) toast.info(`${carryForward.length} pending task(s) carried forward`);
    } catch { setTasks(roleTasks); }
    try {
      const { data: assignedData } = await axios.get(`${API}/staff/daily-tasks/assigned/${role}`, { withCredentials: true });
      const assignedTasks = assignedData.assigned_tasks || [];
      const mgmtTasks = assignedTasks.map(t => ({ category: 'Management Assigned', description: t.description, completed: false }));
      const ids = [...new Set(assignedTasks.map(t => t.task_id).filter(Boolean))];
      setPickedTaskIds(ids);
      setExtraTasks([...mgmtTasks, { category: 'Other', description: '', completed: false }, { category: 'Other', description: '', completed: false }, { category: 'Other', description: '', completed: false }]);
      if (mgmtTasks.length > 0) toast.info(`${mgmtTasks.length} task(s) assigned by management`);
    } catch {
      setPickedTaskIds([]);
      setExtraTasks([{ category: 'Other', description: '', completed: false }, { category: 'Other', description: '', completed: false }, { category: 'Other', description: '', completed: false }]);
    }
    setShowForm(true);
  };

  const handleRoleChange = async (role) => {
    setSelectedRole(role);
    const roleTasksData = await fetchRoleTasks(role);
    const roleTasks = roleTasksData.map(t => ({ ...t, completed: false }));
    setTasks(roleTasks);
    try {
      const { data: assignedData } = await axios.get(`${API}/staff/daily-tasks/assigned/${role}`, { withCredentials: true });
      const assignedTasks = assignedData.assigned_tasks || [];
      const mgmtTasks = assignedTasks.map(t => ({ category: 'Management Assigned', description: t.description, completed: false }));
      const ids = [...new Set(assignedTasks.map(t => t.task_id).filter(Boolean))];
      setPickedTaskIds(ids);
      setExtraTasks([...mgmtTasks, { category: 'Other', description: '', completed: false }, { category: 'Other', description: '', completed: false }, { category: 'Other', description: '', completed: false }]);
    } catch {
      setPickedTaskIds([]);
      setExtraTasks([{ category: 'Other', description: '', completed: false }, { category: 'Other', description: '', completed: false }, { category: 'Other', description: '', completed: false }]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const filledExtras = extraTasks.filter(t => t.description.trim());
    const allTasks = [...tasks, ...filledExtras];
    const payload = { date, role: selectedRole, staff_name: user.name, tasks: allTasks, extra_tasks: filledExtras };
    try {
      if (editId) {
        await axios.put(`${API}/staff/daily-tasks/${editId}`, payload, { withCredentials: true });
        toast.success('Tasks updated');
      } else {
        await axios.post(`${API}/staff/daily-tasks`, payload, { withCredentials: true });
        toast.success('Tasks saved');
        if (pickedTaskIds.length > 0) {
          await axios.post(`${API}/staff/daily-tasks/mark-picked`, { task_ids: pickedTaskIds }, { withCredentials: true });
          setPickedTaskIds([]);
        }
      }
      setShowForm(false);
      fetchData();
      fetchDateDetail(detailDate);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleEdit = (entry) => {
    setDate(entry.date);
    setSelectedRole(entry.role || user.role);
    const entryTasks = entry.tasks || [];
    setTasks(entryTasks.length > 0 ? entryTasks : (FALLBACK_TASKS[entry.role] || []).map(t => ({ ...t, completed: false })));
    const extras = [...(entry.extra_tasks || [])];
    while (extras.length < 3) extras.push({ category: 'Other', description: '', completed: false });
    setExtraTasks(extras);
    setEditId(entry.id);
    setShowForm(true);
  };

  const toggleTask = (idx) => { setTasks(prev => prev.map((t, i) => i === idx ? { ...t, completed: !t.completed } : t)); };
  const toggleExtra = (idx) => { setExtraTasks(prev => prev.map((t, i) => i === idx ? { ...t, completed: !t.completed } : t)); };

  const completedCount = tasks.filter(t => t.completed).length + extraTasks.filter(t => t.description.trim() && t.completed).length;
  const totalCount = tasks.length + extraTasks.filter(t => t.description.trim()).length;

  const filteredEntries = filterRole === 'All' ? entries : entries.filter(e => e.role === filterRole);
  const { sorted, sortKey, sortDir, requestSort } = useSortableData(filteredEntries, 'date', 'desc');

  // Group date detail entries by role
  const detailByRole = {};
  dateDetail.forEach(entry => {
    const role = entry.role || 'unknown';
    if (!detailByRole[role]) detailByRole[role] = [];
    detailByRole[role].push(entry);
  });

  return (
    <div className="space-y-4">
      {/* Date Detail View - All Staff Checklists for a Date */}
      <div className="bg-white rounded-2xl shadow-sm p-5">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-4">
          <h3 className="font-semibold text-[#0A2540] flex items-center">
            <Users className="h-4 w-4 mr-2" /> All Staff Checklists
          </h3>
          <Input type="date" value={detailDate} onChange={e => setDetailDate(e.target.value)} className="w-auto" data-testid="detail-date-picker" />
        </div>

        {dateDetail.length === 0 ? (
          <p className="text-sm text-[#94A3B8]">No tasks logged for {detailDate}</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {DISPLAY_ROLES.map(({ key: roleKey, label: roleLabel }) => {
              const roleEntries = detailByRole[roleKey] || [];
              if (roleEntries.length === 0) return (
                <div key={roleKey} className="bg-[#F8FAFC] rounded-xl p-4 border border-[#E2E8F0]">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${ROLE_COLORS[roleKey]}`}>{roleLabel}</span>
                    <span className="text-sm text-[#94A3B8]">No tasks submitted</span>
                  </div>
                </div>
              );
              // Merge all tasks from all entries for this role
              const allRoleTasks = [];
              let totalDone = 0, totalAll = 0;
              roleEntries.forEach(entry => {
                if (!entry.is_management_assigned) {
                  (entry.tasks || []).forEach(t => { allRoleTasks.push(t); totalAll++; if (t.completed) totalDone++; });
                }
                (entry.extra_tasks || []).filter(t => t.description).forEach(t => { allRoleTasks.push(t); totalAll++; if (t.completed) totalDone++; });
              });
              const pct = totalAll > 0 ? Math.round((totalDone / totalAll) * 100) : 0;
              return (
                <div key={roleKey} className="bg-[#F8FAFC] rounded-xl p-4 border border-[#E2E8F0]" data-testid={`detail-${roleKey}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ROLE_COLORS[roleKey]}`}>{roleLabel}</span>
                      <span className="text-xs text-[#475569]">{roleEntries[0]?.staff_name}</span>
                    </div>
                    <span className="text-sm font-bold" style={{ color: pct === 100 ? '#22c55e' : pct >= 50 ? '#3b82f6' : '#ef4444' }}>
                      {totalDone}/{totalAll} ({pct}%)
                    </span>
                  </div>
                  <div className="w-full bg-[#E2E8F0] rounded-full h-2 mb-3">
                    <div className={`h-2 rounded-full ${pct === 100 ? 'bg-green-500' : pct >= 50 ? 'bg-blue-500' : 'bg-red-400'}`} style={{ width: `${pct}%` }} />
                  </div>
                  <div className="space-y-1.5">
                    {allRoleTasks.map((t, idx) => (
                      <div key={idx} className={`flex items-start gap-2 text-sm px-2 py-1 rounded ${t.completed ? 'bg-green-50' : t.category === 'Management Assigned' ? 'bg-purple-50' : ''}`}>
                        {t.completed ? <CheckSquare className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /> : <Square className="h-4 w-4 text-[#94A3B8] flex-shrink-0 mt-0.5" />}
                        <span className={t.completed ? 'line-through text-[#94A3B8]' : 'text-[#0A2540]'}>
                          {t.description}
                          {t.category === 'Management Assigned' && <span className="text-xs text-purple-600 ml-1">(Mgmt)</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            {/* Also show admin if present */}
            {detailByRole['admin'] && (
              <div className="bg-[#F8FAFC] rounded-xl p-4 border border-[#E2E8F0]" data-testid="detail-admin">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ROLE_COLORS.admin}`}>Admin</span>
                  <span className="text-xs text-[#475569]">{detailByRole['admin'][0]?.staff_name}</span>
                </div>
                {(() => {
                  const adminTasks = [];
                  let d2 = 0, a2 = 0;
                  detailByRole['admin'].forEach(entry => {
                    if (!entry.is_management_assigned) (entry.tasks || []).forEach(t => { adminTasks.push(t); a2++; if (t.completed) d2++; });
                    (entry.extra_tasks || []).filter(t => t.description).forEach(t => { adminTasks.push(t); a2++; if (t.completed) d2++; });
                  });
                  const p2 = a2 > 0 ? Math.round((d2 / a2) * 100) : 0;
                  return (
                    <>
                      <div className="flex items-center justify-between mb-2">
                        <div />
                        <span className="text-sm font-bold" style={{ color: p2 === 100 ? '#22c55e' : p2 >= 50 ? '#3b82f6' : '#ef4444' }}>{d2}/{a2} ({p2}%)</span>
                      </div>
                      <div className="w-full bg-[#E2E8F0] rounded-full h-2 mb-3">
                        <div className={`h-2 rounded-full ${p2 === 100 ? 'bg-green-500' : p2 >= 50 ? 'bg-blue-500' : 'bg-red-400'}`} style={{ width: `${p2}%` }} />
                      </div>
                      <div className="space-y-1.5">
                        {adminTasks.map((t, idx) => (
                          <div key={idx} className={`flex items-start gap-2 text-sm px-2 py-1 rounded ${t.completed ? 'bg-green-50' : ''}`}>
                            {t.completed ? <CheckSquare className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /> : <Square className="h-4 w-4 text-[#94A3B8] flex-shrink-0 mt-0.5" />}
                            <span className={t.completed ? 'line-through text-[#94A3B8]' : 'text-[#0A2540]'}>{t.description}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <h2 className="text-xl font-bold text-[#0A2540]" style={{ fontFamily: 'Outfit, sans-serif' }}>Daily Tasks</h2>
        <div className="flex gap-2">
          <select className="border rounded-lg px-3 py-2 text-sm capitalize" value={filterRole} onChange={e => setFilterRole(e.target.value)} data-testid="task-filter-role">
            <option value="All">All Roles</option>
            {ROLES.map(r => <option key={r} value={r} className="capitalize">{r}</option>)}
          </select>
          <Button onClick={fetchData} variant="outline" size="icon"><RefreshCw className="h-4 w-4" /></Button>
          <Button onClick={initNewForm} className="bg-[#0A2540]" data-testid="add-daily-task-btn">
            <Plus className="h-4 w-4 mr-2" /> New Day
          </Button>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-[#0A2540]">{editId ? 'Edit' : 'New'} Daily Tasks</h2>
              <button onClick={() => setShowForm(false)}><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Date</Label><Input type="date" value={date} onChange={e => setDate(e.target.value)} required data-testid="task-date" /></div>
                <div>
                  <Label>Role</Label>
                  <select className="w-full border rounded-lg px-3 py-2 text-sm capitalize" value={selectedRole} onChange={e => handleRoleChange(e.target.value)} data-testid="task-role-select">
                    {ROLES.map(r => <option key={r} value={r} className="capitalize">{r}</option>)}
                  </select>
                </div>
              </div>
              <div className="bg-[#F1F5F9] rounded-xl p-4">
                <div className="flex justify-between text-sm mb-2">
                  <span className="font-medium text-[#0A2540]">Progress</span>
                  <span className="text-[#475569]">{completedCount}/{totalCount}</span>
                </div>
                <div className="w-full bg-[#E2E8F0] rounded-full h-2">
                  <div className="bg-green-500 h-2 rounded-full transition-all" style={{ width: `${totalCount > 0 ? (completedCount / totalCount) * 100 : 0}%` }} />
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-[#0A2540] mb-2">Tasks</h3>
                <div className="space-y-2">
                  {tasks.map((task, idx) => (
                    <div key={`task-${idx}`} onClick={() => toggleTask(idx)}
                      className={`flex items-start space-x-3 p-3 rounded-lg cursor-pointer transition-colors ${task.completed ? 'bg-green-50' : task.category === 'Carried Forward' ? 'bg-amber-50 border border-amber-200' : task.category === 'Management Assigned' ? 'bg-purple-50 border border-purple-200' : 'bg-[#F8FAFC] hover:bg-[#F1F5F9]'}`}
                      data-testid={`task-${idx}`}
                    >
                      {task.completed ? <CheckSquare className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" /> : <Square className="h-5 w-5 text-[#94A3B8] flex-shrink-0 mt-0.5" />}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-[#94A3B8]">{task.category}</span>
                          {task.category === 'Carried Forward' && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded flex items-center"><ArrowRight className="h-3 w-3 mr-0.5" />Pending</span>}
                          {task.category === 'Management Assigned' && <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">Management</span>}
                        </div>
                        <p className={`text-sm ${task.completed ? 'line-through text-[#94A3B8]' : 'text-[#0A2540]'}`}>{task.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-[#0A2540] mb-2">Extra Tasks</h3>
                <div className="space-y-2">
                  {extraTasks.map((task, idx) => (
                    <div key={`extra-${idx}`} className={`flex items-center space-x-2 ${task.category === 'Management Assigned' ? 'bg-purple-50 rounded-lg p-2 border border-purple-200' : ''}`}>
                      <button type="button" onClick={() => toggleExtra(idx)} className="flex-shrink-0">
                        {task.completed ? <CheckSquare className="h-5 w-5 text-green-600" /> : <Square className="h-5 w-5 text-[#94A3B8]" />}
                      </button>
                      {task.category === 'Management Assigned' ? (
                        <div className="flex-1">
                          <div className="flex items-center gap-1 mb-0.5"><span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">Management</span></div>
                          <p className={`text-sm ${task.completed ? 'line-through text-[#94A3B8]' : 'text-[#0A2540]'}`}>{task.description}</p>
                        </div>
                      ) : (
                        <Input placeholder={`Extra task ${idx + 1}...`} value={task.description}
                          onChange={e => setExtraTasks(prev => prev.map((t, i) => i === idx ? { ...t, description: e.target.value } : t))}
                          className="flex-1" data-testid={`extra-task-${idx}`} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <Button type="submit" className="w-full bg-[#0A2540]" data-testid="task-submit">
                <Save className="h-4 w-4 mr-2" /> {editId ? 'Update' : 'Save'} Tasks
              </Button>
            </form>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F1F5F9]">
              <tr>
                <SortHeader label="Date" sortKey="date" currentKey={sortKey} currentDir={sortDir} onSort={requestSort} />
                <SortHeader label="Role" sortKey="role" currentKey={sortKey} currentDir={sortDir} onSort={requestSort} />
                <SortHeader label="Staff" sortKey="staff_name" currentKey={sortKey} currentDir={sortDir} onSort={requestSort} />
                <th className="px-3 py-3 text-left font-semibold text-[#0A2540]">Progress</th>
                <th className="px-3 py-3 text-left font-semibold text-[#0A2540]">Tasks</th>
                <th className="px-3 py-3 text-right font-semibold text-[#0A2540]">Edit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1F5F9]">
              {loading ? (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-[#94A3B8]">Loading...</td></tr>
              ) : sorted.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-[#94A3B8]">No task entries yet</td></tr>
              ) : sorted.map(entry => {
                const done = entry.summary?.completed || 0;
                const total = done + (entry.summary?.pending || 0);
                const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                return (
                  <tr key={entry.id} className={`hover:bg-[#F8FAFC] ${entry.is_management_assigned ? 'border-l-4 border-purple-500' : ''}`} data-testid={`task-entry-${entry.id}`}>
                    <td className="px-3 py-3 font-medium text-[#0A2540]">{entry.date}</td>
                    <td className="px-3 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${ROLE_COLORS[entry.role] || 'bg-gray-100 text-gray-600'}`}>{entry.role}</span>
                      {entry.is_management_assigned && <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full ml-1">Assigned</span>}
                    </td>
                    <td className="px-3 py-3 text-[#475569]">{entry.staff_name}</td>
                    <td className="px-3 py-3 min-w-[120px]">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-[#E2E8F0] rounded-full h-2">
                          <div className={`h-2 rounded-full ${pct === 100 ? 'bg-green-500' : pct >= 50 ? 'bg-blue-500' : 'bg-amber-500'}`} style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs text-[#475569] whitespace-nowrap">{done}/{total}</span>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(entry.tasks || []).slice(0, 3).map((t, i) => (
                          <span key={`t-${i}`} className={`text-xs px-1.5 py-0.5 rounded ${t.completed ? 'bg-green-100 text-green-700 line-through' : 'bg-red-50 text-red-600'}`}>
                            {t.description?.substring(0, 30)}
                          </span>
                        ))}
                        {(entry.tasks || []).length > 3 && <span className="text-xs text-[#94A3B8]">+{entry.tasks.length - 3}</span>}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <button onClick={() => handleEdit(entry)} className="p-1.5 rounded-lg hover:bg-[#F1F5F9]" data-testid={`edit-task-${entry.id}`}>
                        <Edit2 className="h-4 w-4 text-[#475569]" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DailyTaskModule;
